import { useEcranAide, useDemandeEcran } from './aide/AideContexte';
import React, { useState, useEffect, useCallback } from 'react';
import { departementCourant, peutModifier } from '../services/droits';
import { stageAPI } from '../services/api';
import StageParametrageConfigurations from './StageParametrageConfigurations';
import StageParametrageContacts from './StageParametrageContacts';
import StageParametrageModeles from './StageParametrageModeles';

/**
 * SUIVI DE STAGE — onglet Paramétrage (lot 3.0)
 * =============================================
 * Spec     : doc/modules/suivi_de_stage.md §6
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#tab-parametrage)
 *
 * Trois sous-onglets, comme la maquette :
 *   Configurations ✅ lot 3.0 — la table annuelle et son éditeur à dix étapes
 *   Modèles        ⏳ lots 3.5 / 3.6 / 3.8 — les éditeurs des quatre familles
 *   Contacts       ✅ lot 3.0 — les rôles fonctionnels et les six courriels
 *
 * ── POURQUOI LE CHARGEMENT VIT ICI ──────────────────────────────────────────
 * Les trois sous-onglets partagent la même réponse serveur : les configurations
 * ont besoin des modèles pour leurs listes déroulantes, et les contacts ont
 * besoin de la liste des utilisateurs. Un appel par sous-onglet aurait relu les
 * mêmes lignes à chaque bascule, et deux copies du même tableau finissent
 * toujours par diverger.
 *
 * ⚠️ `recharger` NE PREND AUCUN RAPPEL DE PARENT dans ses dépendances. C'est le
 * défaut le plus grave du lot 1.3 bis du module Étudiants : un rappel de parent
 * dans les dépendances d'un `useCallback` qui recharge EFFACE LA SAISIE EN COURS,
 * parce que la fonction change d'identité à chaque rendu du parent et relance le
 * chargement au milieu d'une frappe. Ici, les écritures renvoient déjà les listes
 * à jour (voir `stageAPI`) : rien n'a besoin de remonter.
 */

const SOUS_ONGLETS = [
    // E135 (lot C.0 de la Certification) : « Processus », comme dans l'onglet Certification.
    // Affichage seulement — la clé, la table et les routes restent « configurations ».
    { cle: 'config',   libelle: 'Processus',      icone: 'fa-diagram-project' },
    { cle: 'modeles',  libelle: 'Modèles',        icone: 'fa-layer-group' },
    // E153 : l'équipe est partie dans « Paramétrage général » ; il reste les courriels des stagiaires.
    { cle: 'contacts', libelle: 'Courriels',      icone: 'fa-envelope' }
];

/**
 * Lot L1a (E169) — DANS UNE VIGNETTE : `ecran` ('config' | 'modeles' | 'contacts') est piloté par les onglets
 * numérotés de la vignette (① Le processus ② Les documents ③ Les courriels) : la barre de sous-onglets disparaît.
 * `configurationId` limite « Le processus » à CE processus ; `onEcrit` prévient l'accueil (compteurs, « reste à
 * faire »), `onSupprime` le ramène à l'accueil quand le processus n'existe plus.
 */
function StageParametrage({ config, user, anneeId, onEquipe, ecran = null, configurationId = null, onEcrit = null, onSupprime = null, manquants = [] }) {
    const [sousOngletLibre, setSousOnglet] = useState('config');
    const sousOnglet = ecran || sousOngletLibre;
    // Aide contextuelle (A1) : « config » s'affiche « Processus », « contacts » s'affiche « Courriels ».
    const VERS_AIDE = { config: 'processus', modeles: 'modeles', contacts: 'courriels' };
    useEcranAide('stagiaires/param/' + (VERS_AIDE[sousOnglet] || sousOnglet));
    useDemandeEcran('stagiaires/param', (s) => {
        const cle = Object.keys(VERS_AIDE).find((k) => VERS_AIDE[k] === s);
        if (cle) setSousOnglet(cle);
    });
    const [donnees, setDonnees]       = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur]         = useState(null);

    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'stage');

    const recharger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true);
        setErreur(null);
        try {
            const r = await stageAPI.getParametrage(departementId, anneeId, configurationId);
            setDonnees(r.data);
        } catch (e) {
            // ⚠️ On NOMME l'échec. Un écran qui reste vide après une erreur réseau
            // se lit comme « il n'y a rien à paramétrer », ce qui est faux.
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message
                || 'Lecture du paramétrage impossible');
            setDonnees(null);
        } finally {
            setChargement(false);
        }
    }, [departementId, anneeId, configurationId]);

    useEffect(() => { recharger(); }, [recharger]);

    /**
     * Les écritures renvoient l'état à jour : on le pose sans relire le serveur.
     * Relire produirait un second aller-retour dont la réponse pourrait arriver
     * après une autre modification — et l'écran afficherait alors l'avant-dernier
     * état sans que rien ne le signale.
     */
    const fusionner = useCallback((morceau) => {
        setDonnees((precedent) => (precedent ? { ...precedent, ...morceau } : precedent));
    }, []);

    if (!departementId) {
        return (
            <div className="stg-encart stg-encart-alerte">
                <i className="fa-solid fa-triangle-exclamation" />
                <span>Aucun département n'est sélectionné : le paramétrage des stages ne peut pas être lu.</span>
            </div>
        );
    }

    return (
        <>
            {/* L2a (E170, maquette rév. 10) : dans une vignette, documents et courriels sont ceux de CE processus — on le dit. */}
            {ecran && ecran !== 'config' && configurationId && (
                <div className="stg-encart stg-encart-info stg-bandeau-propre">
                    <i className="fa-solid fa-link" />
                    <span>
                        <b>{ecran === 'modeles' ? 'Ces modèles de documents' : 'Ces courriels'} sont ceux de
                        « {(donnees?.configurations || []).find((c) => c.id === configurationId)?.titre || 'ce processus'} »</b> — les modifier ne
                        change <b>aucun autre processus</b>. Copiés à la création (des modèles d'origine du département, ou d'un autre
                        processus) ; « Dupliquer » et « Récupérer N-1 » les recopient.
                        {ecran === 'modeles' && <> Pour repartir d'une autre version : « Repartir d'un autre modèle » sur la famille voulue.</>}
                    </span>
                </div>
            )}
            {!ecran && (
            <div className="param-subtab-bar" role="tablist" aria-label="Sous-onglets du paramétrage">
                {SOUS_ONGLETS.map((s) => (
                    <button
                        key={s.cle}
                        type="button"
                        role="tab"
                        aria-selected={sousOnglet === s.cle}
                        className={'param-subtab' + (sousOnglet === s.cle ? ' active' : '')}
                        onClick={() => setSousOnglet(s.cle)}
                    >
                        <i className={'fa-solid ' + s.icone} /> {s.libelle}
                    </button>
                ))}
            </div>
            )}

            {erreur && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{erreur}</span>
                </div>
            )}

            {chargement && !donnees && (
                <div className="stg-attente">
                    <i className="fa-solid fa-circle-notch stg-tourne" />
                    <div>Lecture du paramétrage…</div>
                </div>
            )}

            {donnees && sousOnglet === 'config' && (
                <StageParametrageConfigurations
                    donnees={donnees}
                    departementId={departementId}
                    anneeId={donnees.annee?.id}
                    modifiable={modifiable}
                    onMiseAJour={fusionner}
                    seulement={configurationId}
                    manquants={manquants}
                    onEcrit={onEcrit}
                    onSupprime={onSupprime}
                />
            )}

            {donnees && sousOnglet === 'contacts' && (
                <StageParametrageContacts
                    donnees={donnees}
                    departementId={departementId}
                    modifiable={modifiable}
                    onMiseAJour={fusionner}
                    onEquipe={onEquipe}
                    configurationId={configurationId}
                />
            )}

            {/* Lot 3.8a : le sous-onglet Modèles (écarts E111 → E116). Après une écriture,
                le paramétrage se relit : les listes déroulantes des configurations
                doivent proposer le modèle qu'on vient de créer. */}
            {donnees && sousOnglet === 'modeles' && (
                <StageParametrageModeles departementId={departementId} modifiable={modifiable} onModelesChange={recharger}
                                         configurationId={configurationId} />
            )}
        </>
    );
}

export default StageParametrage;
